import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  amsterdamDate,
  capacityColor,
  fetchCalendarDepartures,
  toSlots,
  type BookingSlot,
} from '../services/bookings';
import BookingCalendarModal from './BookingCalendarModal';

// "Straks geboekt" — the strip under the queue (design/Bookings.dc.html).
//
// The queue says who is standing here NOW; this says what is coming. They
// compete for the same stations, so the only question the desk actually has is
// "can I still take a walk-in, and until when". That is why this is a capacity
// bar and not a list of bookings.
//
// The bar's hue is shared with the booking admin on purpose (services/bookings
// capacityColor): a sold-out slot must look the same in both screens. Note the
// palette collision that buys: on the PC bars to the left red means "something
// is broken", here it means "sold out", which is good news. Mitigated by
// keeping this in its own titled block and using a duller fill than the bars'
// alarm red — if it still reads as an alarm in the room, desaturate the ramp.

/** How many upcoming slots the strip shows before "tik voor de hele kalender". */
const STRIP_ROWS = 4;
/** Refetch cadence. Bookings move on human timescales, not agent ones. */
const POLL_MS = 120_000;

interface Props {
  locationId: string | null;
}

export default function BookingOutlook({ locationId }: Props) {
  const [slots, setSlots] = useState<BookingSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Re-renders the "upcoming" cut as the clock passes each slot.
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    if (!locationId) {
      setSlots([]);
      setLoading(false);
      return;
    }
    try {
      const today = amsterdamDate();
      const rows = await fetchCalendarDepartures(locationId, today, today);
      setSlots(toSlots(rows));
      setFailed(false);
    } catch (err) {
      // Keep the last good view rather than blanking: a blank strip reads as
      // "nothing booked", which is the one wrong answer this must never give.
      console.error('booking outlook fetch failed', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!locationId) return;
    const t = setInterval(() => {
      setNow(Date.now());
      void load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [locationId, load]);

  // Upcoming only. A slot counts as upcoming until it has actually started;
  // the one running right now is the most useful row on the strip, so it stays
  // until the next one begins.
  const upcoming = useMemo(() => {
    const cutoff = now - 20 * 60_000;
    return slots
      .filter((s) => Date.parse(s.slotStart) >= cutoff)
      .slice(0, STRIP_ROWS);
  }, [slots, now]);

  // "Nog N inloopplekken": free places across the slots on the strip. The
  // honest headline, because it is the number that decides whether the person
  // at the desk gets a yes.
  const freePlaces = useMemo(
    () => upcoming.reduce((n, s) => n + Math.max(0, s.capacity - s.booked), 0),
    [upcoming],
  );

  const roomLine = useMemo(() => {
    const rooms = [...new Set(upcoming.flatMap((s) => s.roomNames))].sort();
    const cap = upcoming[0]?.capacity;
    if (rooms.length === 0) return null;
    // The capacity is the location's total for one round, summed across rooms,
    // which is the number that decides whether a walk-in gets a yes.
    return `${rooms.join(' · ')}${cap ? ` · ${cap} plekken per ronde` : ''}`;
  }, [upcoming]);

  if (!locationId) return null;

  return (
    <>
      <button
        type="button"
        className="bk"
        onClick={() => setCalendarOpen(true)}
        title="Open de hele kalender"
      >
        <div className="bk-head">
          <span className="bk-label">STRAKS GEBOEKT</span>
          <span className="qq-spacer" />
          {failed ? (
            <span className="bk-stale">niet opgehaald</span>
          ) : upcoming.length > 0 ? (
            <span className="bk-free">
              nog {freePlaces} inloopplek{freePlaces === 1 ? '' : 'ken'}
            </span>
          ) : null}
          <span className="bk-more">Kalender ›</span>
        </div>

        {loading && slots.length === 0 ? (
          <div className="bk-quiet">Boekingen laden…</div>
        ) : upcoming.length === 0 ? (
          <div className="bk-quiet">Niets meer geboekt vandaag</div>
        ) : (
          <div className="bk-rows">
            {upcoming.map((s) => (
              <div key={s.key} className={`bkslot${s.booked === 0 ? ' bkslot--empty' : ''}`}>
                <span className="bkt">{s.timeOfDay}</span>
                <span className="bktrack">
                  <span
                    className="bkfill"
                    /* A sliver even at zero, so an empty slot reads as "empty"
                       rather than as a row that failed to render. */
                    style={{
                      width: `${Math.max(3, Math.round(s.ratio * 100))}%`,
                      background: capacityColor(s.ratio),
                    }}
                  />
                </span>
                <span className="bkn">{s.booked}/{s.capacity}</span>
                <span className="bkwho">{s.experiencesLabel ?? 'niets geboekt'}</span>
              </div>
            ))}
          </div>
        )}

        {roomLine ? <div className="bk-quiet bk-foot">{roomLine}</div> : null}
      </button>

      {calendarOpen ? (
        <BookingCalendarModal locationId={locationId} onClose={() => setCalendarOpen(false)} />
      ) : null}
    </>
  );
}
